import axios from "axios";
import { getUserAuth } from "../helpers";
import { REACT_APP_NAV_ROUTES } from "../router/AppNavigation";
import { config } from "../config";

export const API_BASE_URL = config.api.baseUrl;

export const OTHER_USER_ACCESS_SUB_DOMAIN = config.domains.loginAsUserUrl;

export const api = axios.create({
  baseURL: API_BASE_URL,
});

// export const baileysClient = axios.create({
//   baseURL: BAILEYS_WHATSAPP_BASE_URL,
// });

export const imageApi = axios.create({
  baseURL: config.assets.s3BaseUrl,
});

export const baseAxios = axios.create({
  baseURL: API_BASE_URL,
});

/* ------------------------------------------------------------------ */
/* State */
/* ------------------------------------------------------------------ */

let isRedirecting = false;

/* ------------------------------------------------------------------ */
/* Helpers */
/* ------------------------------------------------------------------ */

const clearSessionAndRedirect = (url: string, message?: string) => {
  if (isRedirecting) return;

  isRedirecting = true;

  localStorage.clear();

  if (message) {
    window.alert(message);
  }

  window.location.href = url;
};

/* ------------------------------------------------------------------ */
/* Prevent redirect loop */
/* ------------------------------------------------------------------ */

const isOnErrorPage = (): boolean => {
  const path = window.location.pathname;

  return [
    "/company-not-assigned",
    REACT_APP_NAV_ROUTES.subscription.choose,
    "/subscribe",
    "/auth/login",
  ].some((route) => path.startsWith(route));
};

/** Routes that must work without login; avoid session-clear redirects on API errors. */
const isPublicStandalonePage = (): boolean => {
  const path = window.location.pathname;
  return (
    path.startsWith("/public/review") || path.startsWith("/public-review")
  );
};

/* ------------------------------------------------------------------ */
/* Subscription redirect */
/* ------------------------------------------------------------------ */

export const handleSubscriptionRedirect = (code: number, message: string) => {
  if (isRedirecting || isOnErrorPage()) return;

  isRedirecting = true;

  const numericCode = Number(code);

  // Encode the message to safely pass it as a URL parameter
  const encodedMessage = encodeURIComponent(message || "");
  
  let url = `${REACT_APP_NAV_ROUTES.subscription.pricing}?code=${numericCode}`;
  
  if (encodedMessage) {
    url += `&message=${encodedMessage}`;
  }

  // if ([1001, 1005, 1004].includes(numericCode)) {
  //   url = `${REACT_APP_NAV_ROUTES.subscription.pricing}${numericCode === 1001 ? "" : `?code=${numericCode}`}`;
  // }
  window.history.replaceState(null, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
  setTimeout(() => {
    isRedirecting = false;
  }, 300);
};

/* ------------------------------------------------------------------ */
/* Request Interceptor */
/* ------------------------------------------------------------------ */

api.interceptors.request.use((config) => {
  const accessToken = getUserAuth();

  if (accessToken && config.headers) {
    config.headers["auth-Token"] = accessToken;
  }

  return config;
});

/* ------------------------------------------------------------------ */
/* Response Interceptor */
/* ------------------------------------------------------------------ */

api.interceptors.response.use(
  (response) => {
    const { data } = response;

    if (data?.success === false && String(data?.code) === "401") {
      if (!isOnErrorPage() && !isPublicStandalonePage()) {
        clearSessionAndRedirect("/company-not-assigned?code=401");
      }
      return Promise.reject(data);
    }
    return response;
  },
  (error) => {
    console.error("API Error:", error);
    const status = error.response?.status;
    const errorData = error.response?.data;
    const code = errorData?.code;
    const message = errorData?.message;

    /* ---------------- Session Expired ---------------- */

    if (status === 401) {
      if (errorData?.message === 403 && errorData?.data?.includes("limit")) {
        return Promise.reject(error);
      }

      if (!isPublicStandalonePage()) {
        clearSessionAndRedirect(
          "/auth/login",
          "Attention: Your session has expired. Please log in again to continue."
        );
      }

      return Promise.reject(error);
    }

    /* ---------------- Subscription Errors ---------------- */

    if (SUBSCRIPTION_ERROR_CODES.includes(Number(code)) && !isPublicStandalonePage()) {
      handleSubscriptionRedirect(code, message);
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

/* ------------------------------------------------------------------ */
/* Subscription Error Messages */
/* ------------------------------------------------------------------ */

export const SUBSCRIPTION_ERROR_MESSAGE: Record<number, string> = {
  1001: "No active subscription found. Please subscribe to access this feature.",
  1002: "Your subscription has expired. Please renew your subscription to continue.",
  1003: "Payment is required to activate or continue your subscription.",
  1004: "You have reached the usage limit for your current subscription plan.",
  1005: "This feature is not included in your current subscription plan.",
  1006: "Your free trial has expired. Please upgrade to a paid plan to continue.",
  1007: "This feature is available on higher plans. Please upgrade your subscription to continue.",
  1008: "Your subscription is currently inactive. Please contact support or renew your plan.",
};

export const SUBSCRIPTION_ERROR_CODES: number[] = Object.keys(
  SUBSCRIPTION_ERROR_MESSAGE,
).map(Number);
